/*
Resources on colour palettes

Generates qualitative palettes
http://tools.medialab.sciences-po.fr/iwanthue/

Nice properties for colourmaps to have (from matplotlib).
https://matplotlib.org/users/colormaps.html#colorcet (linear_bgy_10_95_c74_n256)
https://bids.github.io/colormap/

And a palette generator from Google
http://google.github.io/palette.js/
*/

import * as globals from '@wandb/weave/common/css/globals.styles';
import {
  colorFromName,
  colorN,
  COLORS16_IN_SPECTRUM_ORDER,
  COMMON_DEUTERONOMALY_PALETTE_CYCLE_ORDER,
  COMMON_DEUTERONOMALY_PALETTE_SPECTRUM_ORDER,
  type DefaultColor,
  OTHER_COLOR_BLIND_PALETTE_CYCLE_ORDER,
  OTHER_COLOR_BLIND_PALETTE_SPECTRUM_ORDER,
  type RGB,
  ROBIN16,
} from '@wandb/weave/common/util/colors';
import Color from 'color';
import _ from 'lodash';

import {
  RunColorOptions,
  type SemanticLegendSettingsWithCategoricalConfig,
  YAxisValues,
} from '../components/WorkspaceDrawer/Settings/types';
import {getRunColorGroupResolvedColor} from './runColorGroup';
import * as Run from './runs';
import type * as RunTypes from './runTypes';
import {type Key} from './runTypes';
import type {RunColorConfig} from './section';
import {padToLength} from './string';
export * from '@wandb/weave/common/util/colors';

export type RGBA = [number, number, number, number];

export function color(index: number, alpha?: number) {
  /**
   * Legacy coloring function. Returns one of 10 "original" colors
   * based on the provided index.
   */
  return colorN(index, COLORS10, alpha);
}

export enum ColorPaletteOption {
  DEFAULT,
  COMMON_DEUTERONOMALY,
  OTHER_COLOR_BLIND_PALETTE,
}

export function getColorPaletteOptionTitle(
  colorPaletteOption: ColorPaletteOption
): string {
  switch (colorPaletteOption) {
    case ColorPaletteOption.COMMON_DEUTERONOMALY:
      return 'Colorblind-safe (deuteranomaly)';
    case ColorPaletteOption.OTHER_COLOR_BLIND_PALETTE:
      return 'Colorblind-safe (all other forms)';
    case ColorPaletteOption.DEFAULT:
      return 'Default';
  }
}
export function getColorPalette(
  colorPaletteOption: ColorPaletteOption
): string[] {
  switch (colorPaletteOption) {
    case ColorPaletteOption.COMMON_DEUTERONOMALY:
      return COMMON_DEUTERONOMALY_PALETTE_CYCLE_ORDER;
    case ColorPaletteOption.OTHER_COLOR_BLIND_PALETTE:
      return OTHER_COLOR_BLIND_PALETTE_CYCLE_ORDER;
    case ColorPaletteOption.DEFAULT:
      return ROBIN16;
  }
}

export function getColorPaletteSpectrumOrder(
  colorPaletteOption: ColorPaletteOption
): readonly string[] {
  switch (colorPaletteOption) {
    case ColorPaletteOption.COMMON_DEUTERONOMALY:
      return COMMON_DEUTERONOMALY_PALETTE_SPECTRUM_ORDER;
    case ColorPaletteOption.OTHER_COLOR_BLIND_PALETTE:
      return OTHER_COLOR_BLIND_PALETTE_SPECTRUM_ORDER;
    case ColorPaletteOption.DEFAULT:
      return COLORS16_IN_SPECTRUM_ORDER;
  }
}

type RunColorArgsBase = {
  customRunColors: RunColorConfig | string | undefined;
  semanticLegendSettings?: SemanticLegendSettingsWithCategoricalConfig;
  alpha?: number;
  useGroupDefaultIndex?: boolean;
  colorPaletteOption: ColorPaletteOption;
};

type RunColorArgsNoGroups = RunColorArgsBase & {
  // these are the only properties needed to compute run color when there's no grouping
  run: Pick<RunTypes.Run, 'name' | 'defaultColorIndex'>;
  groupKeys?: undefined;
};
type RunColorArgsWithGroups = RunColorArgsBase & {
  run: Pick<RunTypes.Run, 'defaultColorIndex'> & Run.RunWithUniqueId;
  groupKeys: Key[];
};
export type RunColorArgs = RunColorArgsNoGroups | RunColorArgsWithGroups;

export type RunColorSource = 'automatic' | 'explicit' | 'inherited';

export type ResolvedRunColorState = {
  color: string;
  source: RunColorSource;
  parentInheritedColor?: string;
};

export function colorStringWithAlpha(customColor: string, alpha?: number) {
  const c = Color(customColor);
  return c
    .alpha(alpha ?? c.alpha())
    .rgb()
    .string();
}

export function resolveRunColorState({
  run,
  groupKeys,
  customRunColors,
  semanticLegendSettings,
  alpha,
  useGroupDefaultIndex,
  colorPaletteOption,
}: RunColorArgs): ResolvedRunColorState {
  const runColorKey = groupKeys ? Run.uniqueId(run, groupKeys) : run.name;

  if (typeof customRunColors === 'string' && !_.isEmpty(customRunColors)) {
    return {
      color: customRunColors,
      source: 'explicit',
    };
  }

  const hasActiveGrouping = groupKeys != null && groupKeys.length > 0;

  // Use a custom-set color, if one exists.
  let parentInheritedColor: string | undefined;
  if (typeof customRunColors === 'object') {
    if (hasActiveGrouping) {
      const customGroupColor = getRunColorGroupResolvedColor(
        customRunColors,
        run,
        groupKeys
      );
      parentInheritedColor = customGroupColor?.parentInheritedColor;
      if (customGroupColor != null) {
        return {
          color: colorStringWithAlpha(customGroupColor.color, alpha),
          source: customGroupColor.source,
          parentInheritedColor,
        } satisfies ResolvedRunColorState;
      }
    }

    const customColor = customRunColors[runColorKey];
    if (customColor != null) {
      return {
        color: colorStringWithAlpha(customColor, alpha),
        source: 'explicit',
        parentInheritedColor,
      };
    }
  }

  /**
   * Semantic legend colors are currently disable for when grouping is enabled
   * If grouping isn't enabled, use key-based colors if the semantic legend is enabled.
   */
  if (
    semanticLegendSettings?.runColorOptions ===
      RunColorOptions.KeyBasedColors &&
    !hasActiveGrouping
  ) {
    if (semanticLegendSettings?.yAxisValue === YAxisValues.Config_Categories) {
      return {
        color: getRunCategoryColor(run, semanticLegendSettings, alpha),
        source: 'automatic',
        parentInheritedColor,
      };
    }
    return {
      color: getColorFromSemanticLegend(run, semanticLegendSettings, alpha),
      source: 'automatic',
      parentInheritedColor,
    };
  }

  // Use the color-at-birth whenever there's no custom color
  // and no grouping involved.
  const useColorAtBirth = groupKeys == null || _.isEmpty(groupKeys);

  /**
   * When randomizing colors that are grouped, we need a way to force them into round-robin colors instead of falling back to using the color from name method. The `useGroupDefaultIndex` flag is used to indicate that the colors are being randomized for a grouped run.
   */
  const useGroupRandomization = useGroupDefaultIndex;
  const colorPalette = getColorPalette(colorPaletteOption);

  if (
    run.defaultColorIndex != null &&
    (useGroupRandomization || useColorAtBirth)
  ) {
    return {
      color: colorN(run.defaultColorIndex, colorPalette, alpha),
      source: 'automatic',
      parentInheritedColor,
    };
  }

  // If this run is from before we assigned colors at birth,
  // use a hash of the name.
  return {
    color: colorFromName(runColorKey, alpha, colorPalette),
    source: 'automatic',
    parentInheritedColor,
  };
}

export function runColor(args: RunColorArgs) {
  return resolveRunColorState(args).color;
}

export function getColorName(c = ''): string {
  const colorDetails = getRunColorDetails(c);
  return colorDetails?.name ?? c;
}

export function getTextColor(c = ''): string {
  const colorDetails = getRunColorDetails(c);
  return colorDetails?.textColor ?? c;
}

// Got this list from a cool site http://tools.medialab.sciences-po.fr/iwanthue/
// First five are locked from demian
// Try to make them lighter towards the end so they fade a little into white background
export const COLORS10 = [
  globals.darkBlue,
  '#df672a',
  '#c1433c',
  '#3d9e3e',
  '#ecbb33',
  '#926ccb',
  '#6bb59b',
  '#ad825c',
  '#c66b9e',
  '#a7b756',
];

interface RunColorDetails {
  name: string;
  textColor: string;
}

// dictionary mapped from run color (for lines, shaded are, etc.)
// to name and text color (for legend and tooltip)
const runColorDict: {[key in DefaultColor]: RunColorDetails} = {
  '#F07FDD': {name: 'pink', textColor: globals.runPinkText},
  '#C2327A': {name: 'maroon', textColor: globals.runMaroonText},
  '#F0434F': {name: 'red', textColor: globals.runRedText},
  '#FAB796': {name: 'peach', textColor: globals.runPeachText},
  '#E57439': {name: 'orange', textColor: globals.runOrangeText},
  '#FFB83D': {name: 'yellow', textColor: globals.runYellowText},
  '#9BC750': {name: 'lime', textColor: globals.runLimeText},
  '#479A5F': {name: 'kelly green', textColor: globals.runKellyGreenText},
  '#87CEBF': {name: 'seafoam', textColor: globals.runSeafoamText},
  '#229487': {name: 'forest', textColor: globals.runForestText},
  '#5BC5DB': {name: 'cyan', textColor: globals.runCyanText},
  '#538AE5': {name: 'blue', textColor: globals.runBlueText},
  '#865ED6': {name: 'purple', textColor: globals.runPurpleText},
  '#DC4BDC': {name: 'magenta', textColor: globals.runMagentaText},
  '#AD6F50': {name: 'brown', textColor: globals.runBrownText},
  '#A1A9AD': {name: 'gray', textColor: globals.runGrayText},
};

export function getRunColorDetails(c: string): RunColorDetails | undefined {
  const hexColor = c.startsWith('rgb') ? Color(c).hex() : c;
  if (hexColor in runColorDict) {
    return runColorDict[hexColor as DefaultColor];
  }
  return undefined;
}

// https://bids.github.io/colormap/
const _PLASMA_GRADIENT_WITHOUT_ALPHA: RGB[] = [
  [13, 8, 135],
  [16, 7, 136],
  [19, 7, 137],
  [22, 7, 138],
  [25, 6, 140],
  [27, 6, 141],
  [29, 6, 142],
  [32, 6, 143],
  [34, 6, 144],
  [36, 6, 145],
  [38, 5, 145],
  [40, 5, 146],
  [42, 5, 147],
  [44, 5, 148],
  [46, 5, 149],
  [47, 5, 150],
  [49, 5, 151],
  [51, 5, 151],
  [53, 4, 152],
  [55, 4, 153],
  [56, 4, 154],
  [58, 4, 154],
  [60, 4, 155],
  [62, 4, 156],
  [63, 4, 156],
  [65, 4, 157],
  [67, 3, 158],
  [68, 3, 158],
  [70, 3, 159],
  [72, 3, 159],
  [73, 3, 160],
  [75, 3, 161],
  [76, 2, 161],
  [78, 2, 162],
  [80, 2, 162],
  [81, 2, 163],
  [83, 2, 163],
  [85, 2, 164],
  [86, 1, 164],
  [88, 1, 164],
  [89, 1, 165],
  [91, 1, 165],
  [92, 1, 166],
  [94, 1, 166],
  [96, 1, 166],
  [97, 0, 167],
  [99, 0, 167],
  [100, 0, 167],
  [102, 0, 167],
  [103, 0, 168],
  [105, 0, 168],
  [106, 0, 168],
  [108, 0, 168],
  [110, 0, 168],
  [111, 0, 168],
  [113, 0, 168],
  [114, 1, 168],
  [116, 1, 168],
  [117, 1, 168],
  [119, 1, 168],
  [120, 1, 168],
  [122, 2, 168],
  [123, 2, 168],
  [125, 3, 168],
  [126, 3, 168],
  [128, 4, 168],
  [129, 4, 167],
  [131, 5, 167],
  [132, 5, 167],
  [134, 6, 166],
  [135, 7, 166],
  [136, 8, 166],
  [138, 9, 165],
  [139, 10, 165],
  [141, 11, 165],
  [142, 12, 164],
  [143, 13, 164],
  [145, 14, 163],
  [146, 15, 163],
  [148, 16, 162],
  [149, 17, 161],
  [150, 19, 161],
  [152, 20, 160],
  [153, 21, 159],
  [154, 22, 159],
  [156, 23, 158],
  [157, 24, 157],
  [158, 25, 157],
  [160, 26, 156],
  [161, 27, 155],
  [162, 29, 154],
  [163, 30, 154],
  [165, 31, 153],
  [166, 32, 152],
  [167, 33, 151],
  [168, 34, 150],
  [170, 35, 149],
  [171, 36, 148],
  [172, 38, 148],
  [173, 39, 147],
  [174, 40, 146],
  [176, 41, 145],
  [177, 42, 144],
  [178, 43, 143],
  [179, 44, 142],
  [180, 46, 141],
  [181, 47, 140],
  [182, 48, 139],
  [183, 49, 138],
  [184, 50, 137],
  [186, 51, 136],
  [187, 52, 136],
  [188, 53, 135],
  [189, 55, 134],
  [190, 56, 133],
  [191, 57, 132],
  [192, 58, 131],
  [193, 59, 130],
  [194, 60, 129],
  [195, 61, 128],
  [196, 62, 127],
  [197, 64, 126],
  [198, 65, 125],
  [199, 66, 124],
  [200, 67, 123],
  [201, 68, 122],
  [202, 69, 122],
  [203, 70, 121],
  [204, 71, 120],
  [204, 73, 119],
  [205, 74, 118],
  [206, 75, 117],
  [207, 76, 116],
  [208, 77, 115],
  [209, 78, 114],
  [210, 79, 113],
  [211, 81, 113],
  [212, 82, 112],
  [213, 83, 111],
  [213, 84, 110],
  [214, 85, 109],
  [215, 86, 108],
  [216, 87, 107],
  [217, 88, 106],
  [218, 90, 106],
  [218, 91, 105],
  [219, 92, 104],
  [220, 93, 103],
  [221, 94, 102],
  [222, 95, 101],
  [222, 97, 100],
  [223, 98, 99],
  [224, 99, 99],
  [225, 100, 98],
  [226, 101, 97],
  [226, 102, 96],
  [227, 104, 95],
  [228, 105, 94],
  [229, 106, 93],
  [229, 107, 93],
  [230, 108, 92],
  [231, 110, 91],
  [231, 111, 90],
  [232, 112, 89],
  [233, 113, 88],
  [233, 114, 87],
  [234, 116, 87],
  [235, 117, 86],
  [235, 118, 85],
  [236, 119, 84],
  [237, 121, 83],
  [237, 122, 82],
  [238, 123, 81],
  [239, 124, 81],
  [239, 126, 80],
  [240, 127, 79],
  [240, 128, 78],
  [241, 129, 77],
  [241, 131, 76],
  [242, 132, 75],
  [243, 133, 75],
  [243, 135, 74],
  [244, 136, 73],
  [244, 137, 72],
  [245, 139, 71],
  [245, 140, 70],
  [246, 141, 69],
  [246, 143, 68],
  [247, 144, 68],
  [247, 145, 67],
  [247, 147, 66],
  [248, 148, 65],
  [248, 149, 64],
  [249, 151, 63],
  [249, 152, 62],
  [249, 154, 62],
  [250, 155, 61],
  [250, 156, 60],
  [250, 158, 59],
  [251, 159, 58],
  [251, 161, 57],
  [251, 162, 56],
  [252, 163, 56],
  [252, 165, 55],
  [252, 166, 54],
  [252, 168, 53],
  [252, 169, 52],
  [253, 171, 51],
  [253, 172, 51],
  [253, 174, 50],
  [253, 175, 49],
  [253, 177, 48],
  [253, 178, 47],
  [253, 180, 47],
  [253, 181, 46],
  [254, 183, 45],
  [254, 184, 44],
  [254, 186, 44],
  [254, 187, 43],
  [254, 189, 42],
  [254, 190, 42],
  [254, 192, 41],
  [253, 194, 41],
  [253, 195, 40],
  [253, 197, 39],
  [253, 198, 39],
  [253, 200, 39],
  [253, 202, 38],
  [253, 203, 38],
  [252, 205, 37],
  [252, 206, 37],
  [252, 208, 37],
  [252, 210, 37],
  [251, 211, 36],
  [251, 213, 36],
  [251, 215, 36],
  [250, 216, 36],
  [250, 218, 36],
  [249, 220, 36],
  [249, 221, 37],
  [248, 223, 37],
  [248, 225, 37],
  [247, 226, 37],
  [247, 228, 37],
  [246, 230, 38],
  [246, 232, 38],
  [245, 233, 38],
  [245, 235, 39],
  [244, 237, 39],
  [243, 238, 39],
  [243, 240, 39],
  [242, 242, 39],
  [241, 244, 38],
  [241, 245, 37],
  [240, 247, 36],
  [240, 249, 33],
];

const _PLASMA_GRADIENT: RGBA[] = _PLASMA_GRADIENT_WITHOUT_ALPHA.map(rgb => [
  ...rgb,
  1,
]);

// we cut off the light colours at the end because they are hard to see on a white background
export const PLASMA_GRADIENT = _.slice(
  _PLASMA_GRADIENT,
  0,
  Math.round(0.9 * _PLASMA_GRADIENT.length)
);

export function toHex(rgbVal: number): string {
  const roundedAndClampedVal = _.clamp(Math.round(rgbVal), 0, 255);
  const hexStr = roundedAndClampedVal.toString(16);
  return padToLength(hexStr, 2, '0');
}

export const PLASMA_GRADIENT_SPARSE_HEX: string[] = [];
const N_SPARSE = 8;
for (let i = 0; i < N_SPARSE; i++) {
  const colIndex = Math.floor(
    (i * (PLASMA_GRADIENT.length - 1)) / (N_SPARSE - 1)
  );
  const c = PLASMA_GRADIENT[colIndex];
  PLASMA_GRADIENT_SPARSE_HEX.push(
    `#${toHex(c[0])}${toHex(c[1])}${toHex(c[2])}`
  );
}

function getColorFromSemanticLegend(
  run: {name: string},
  semanticLegendSettings: SemanticLegendSettingsWithCategoricalConfig,
  alpha?: number
) {
  let c = Color(semanticLegendSettings?.excludedRunColor);
  let val = 0;
  if (semanticLegendSettings?.yAxisValue === YAxisValues.Config_Number) {
    const configVal = semanticLegendSettings?.configValuesForRun?.find(
      c => c.runName === run.name
    );
    if (configVal != null) {
      val = configVal.value;
    } else {
      return c
        .alpha(alpha ?? c.alpha())
        .rgb()
        .string();
    }
  } else {
    const metricStat = semanticLegendSettings?.metricStatisticsForRun?.find(
      m => m.run_name === run.name
    );

    // If the run is not in the metric statistics, use the excluded run color.
    if (metricStat == null) {
      return c
        .alpha(alpha ?? c.alpha())
        .rgb()
        .string();
    } else {
      switch (semanticLegendSettings?.yAxisValue) {
        case 'min':
          val = metricStat?.min;
          break;
        case 'max':
          val = metricStat?.max;
          break;
        case 'last':
          val = metricStat?.last;
          break;
        default:
          break;
      }
    }
  }

  // find the bucket that the run's metric value falls into.
  const bucketIndex = semanticLegendSettings?.buckets?.findIndex(bucket => {
    return val <= (bucket.bucketMax ?? 0);
  });

  // If the run's metric value is in a bucket, use the bucket's color.
  if (bucketIndex !== -1) {
    c = Color(
      semanticLegendSettings?.bucketCustomizations?.[bucketIndex ?? 0]?.color ??
        semanticLegendSettings?.buckets?.[bucketIndex ?? 0]?.color ??
        semanticLegendSettings?.excludedRunColor
    );
  } else {
    // If the run's metric value is greater than the last bucket's max, use the last bucket's color.
    if (
      val >
      (semanticLegendSettings?.buckets?.[
        semanticLegendSettings?.buckets?.length - 1
      ]?.bucketMax ?? 0)
    ) {
      c = Color(
        semanticLegendSettings?.buckets?.[
          semanticLegendSettings?.buckets?.length - 1
        ]?.color ?? semanticLegendSettings?.excludedRunColor
      );
    }
  }
  return c
    .alpha(alpha ?? c.alpha())
    .rgb()
    .string();
}

function getRunCategoryColor(
  run: {name: string},
  semanticLegendSettings: SemanticLegendSettingsWithCategoricalConfig,
  alpha?: number
) {
  let c = Color(semanticLegendSettings?.excludedRunColor);
  if (semanticLegendSettings?.categoryRunColorMap?.[run.name]) {
    c = Color(semanticLegendSettings?.categoryRunColorMap?.[run.name]);
  }
  return c
    .alpha(alpha ?? c.alpha())
    .rgb()
    .string();
}
